import { LightningElement, api, wire } from "lwc";
import { getAccessToken } from "@devforce/trailhead-client-auth";
import { EffectAdapter } from "common/effectAdapter";
import { logger } from "@devforce/trailhead-client-logger";
import { getLocale } from "@devforce/trailhead-client-locale";
import { getJSON, postJSON } from "@devforce/trailhead-client-fetch";
import * as locales from "./locales";
import { getClient } from "@devforce/trailhead-client-api";
import { showToast } from "tds/toaster";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { Assessment, SelectedAnswers } from "elements/tds/enhancedQuiz/types";
import { trackUserActivity } from "common/track";

function getDefaultCsrf() {
  try {
    return document
      .querySelector('meta[name="csrf-token"]')!
      .getAttribute("content")!;
  } catch (error) {
    return "";
  }
}

export type CompleteQuizEvent = {
  detail: {
    assessment_state: Assessment["assessment_state"];
    call_to_action: Assessment["call_to_action"];
    legacy_rewards: Assessment["legacy_rewards"];
  };
};

export default class EnhancedQuiz extends LightningElement {
  @api contentId: string | null = null;
  @api moduleId: string | null = null;

  // When loaded in a mobile browser, disables the submit button and blurs the quiz with an error.
  @api desktopOnly: boolean = false;

  // Disables submit button when the user still needs to connect their account to a third-party service (like Tableau)
  @api connectedAccountRequired = false;

  // Enables ability to retake the quiz
  @api retakeEnabled = false;

  @api handleError: (e: unknown) => void = () => {};

  private assessmentData = {};
  private anonymous = true;
  private assessmentLoaded = false;
  private assessmentLoading = true;
  private submitting = false;
  private fromTrailhead = getClient();
  private cachedContentId = "";

  handleComplete(event: CompleteQuizEvent) {
    this.dispatchEvent(new CustomEvent("complete", { detail: event.detail }));
  }

  handleRetakeComplete() {
    this.dispatchEvent(new CustomEvent("retakecomplete"));
  }

  handleRetakeStart() {
    trackUserActivity(this, "quiz_retake_clicked", {
      unit_content_uid: this.contentId || "",
      module_content_uid: this.moduleId || "",
      pageLocation: "th-enhanced-quiz",
    });
  }

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = {};

  @wire(EffectAdapter, { contentId: "$contentId" })
  private async getAssessment() {
    // Only fetch the assessment if the content id has changed
    if (this.contentId && this.cachedContentId !== this.contentId) {
      this.cachedContentId = this.contentId;
      try {
        const accessToken = await getAccessToken();
        if (accessToken) this.anonymous = false;
        this.assessmentData = await getJSON<Assessment>(
          `/content_assessments/${this.contentId}`,
          {
            headers: { "Accept-Language": getLocale() },
          }
        );
        this.assessmentLoaded = true;
      } catch (e) {
        logger.error(e);
        showToast({
          message: this.labels.loadingError,
          variant: "error",
        });
      } finally {
        this.assessmentLoading = false;
      }
    }
  }

  private async handleSubmit(
    event: CustomEvent<{ selectedAnswers: SelectedAnswers }>
  ) {
    event.preventDefault();
    event.stopPropagation();
    const body = JSON.parse(JSON.stringify(event.detail.selectedAnswers));
    const queryParams = new URLSearchParams(window.location.search);
    const breadcrumbs = {
      trail_id: queryParams.get("trail_id"),
      trailmix_creator_id: queryParams.get("trailmix_creator_id"),
      trailmix_slug: queryParams.get("trailmix_slug"),
    };
    try {
      this.submitting = true;
      const accessToken = await getAccessToken();
      if (accessToken) this.anonymous = false;
      this.assessmentData = await postJSON<SelectedAnswers>(
        `/content_assessments/${this.contentId}`,
        { answers: body, breadcrumbs },
        {
          headers: {
            "X-CSRF-TOKEN": getDefaultCsrf(),
            "Accept-Language": getLocale(),
          },
          method: "PUT",
        }
      );
    } catch (e) {
      logger.error(e);
      showToast({
        message: this.labels.loadingError,
        variant: "error",
      });
    } finally {
      this.submitting = false;
    }
  }
}
